<!--
  MediaCooperationItemMasterSetting.vue
  媒体連携項目マスター設定モーダル
  HRモンスター内での応募者情報項目名およびデータと、媒体側の項目名およびデータを紐づける設定画面
  媒体連携処理で、媒体側の応募者情報項目に該当項目がある場合、項目データを共通項目データに置き換えるための設定

  coller
    ManageMainView.vue
-->
<template>
  <ModalBase
    :title="'媒体連携項目マスター設定'"
    :isShowModal="isShowModal"
    :canCancel="false"
    :width="'calc(100vw - 100px)'"
    :height="'calc(100vh - 45px)'"
    :close="modalClose"
  >
    <!-- 処理中モーダル -->
    <LoadingModal
      v-if="isResponseWaiting"
      :dispTitle="loadingTitle"
      :isShowModal="isResponseWaiting"
      :dispMessage="loadingMessage"
    />
    <!-- アラートモーダル -->
    <AlertModal
      v-if="isAlertOpen"
      :isShowModal="isAlertOpen"
      :alertData="alertData"
      :alertResult="alertResult"
    />
    <!-- 媒体連携項目マスター設定モーダル -->
    <div class="master-setting-modal" v-if="currentMaster">
      <!-- 媒体連携項目マスター操作 -->
      <fieldset :disabled="isResponseWaiting">
        <div class="master-setting-header" ref="settingHeader">
          <!-- 既存設定ありの場合はプルダウンを表示 -->
          <div class="master-select-area">
            <!-- マスター名変更表示 -->
            <div class="name-disp" v-if="isNameEditing() === true">
              <span class="is-small">マスター名：</span>
              <!-- 設定名入力 -->
              <input class="input setting-name" v-model="masterName" />
              <!-- 項目マスター表示順 -->
              <div v-if="isTemplateCreate() == false">
                <span class="is-small">表示順：</span>
                <Tooltip :message="masterOrderList()">
                  <input
                    type="number"
                    step="1"
                    min="1"
                    :max="mediaCooperationItemMasterNameList.length"
                    class="input is-small master-order"
                    :disabled="mediaCooperationItemMasterNameList.length <= 1"
                    v-model="currentMasterOrder"
                    @change="changeOrderNo()"
                  />
                </Tooltip>
              </div>
              <!-- 保存およびキャンセル (項目マスター名編集中にのみ表示) -->
              <div v-if="isEditName === true">
                <!-- 設定名保存 -->
                <button class="name-save button is-small is-primary" @click="saveName()">
                  変更保存
                </button>
                <!-- 設定名変更キャンセル -->
                <button class="name-edit-cancel button is-small" @click="editNameCancel()">
                  キャンセル
                </button>
              </div>
            </div>
            <!-- マスター名選択表示 (未編集時のみ表示) -->
            <div class="name-disp" v-else>
              <!-- 媒体項目マスター選択プルダウン -->
              <select class="setting-select" v-model="currentMasterId" @change="masterSelect()" :disabled="!currentMasterId">
                <option
                  v-for="(itemMaster, masterIndex) in mediaCooperationItemMasterNameList"
                  :key="itemMaster.id + '_' + masterIndex"
                  :value="itemMaster.id"
                >
                  {{ itemMaster.master_name }}
                </option>
              </select>
              <!-- 名称変更ボタン -->
              <button class="name-edit button is-small" :disabled="!currentMasterId" @click="editName()">
                名称変更
              </button>
              <!-- 有効/無効 -->
              <button class="toggle-apply-flag button is-small" :class="{'is-primary': isWorking === true}" disabled="true">
                {{isWorking ? '有効中' : '無効中'}}
              </button>
              <!-- 紐づけロボ確認 -->
              <button class="name-edit button is-small" @click="confirmedRoboLink()" :disabled="linkedRoboList === ''">
                紐付け確認
              </button>
            </div>
          </div>
          <!-- 操作パネル (マスター名編集中は表示しない) -->
          <div class="operation-button-area" v-if="isPanelDisp() === true">
            <!-- 未編集時の表示 -->
            <div class="master-edit-area" v-if="isMasterEditing() === false">
              <!-- カスタムカラムが1つもない場合、fieldsetごと非活性にする -->
              <fieldset :disabled="companyColumnSetting.length === 0">
                <!-- 設定編集ボタン -->
                <button class="master-edit button is-small" :class="{'is-primary': isMasterEditing() === false}" :disabled="!currentMasterId" @click="editMaster()">
                  マスター編集
                </button>
                <!-- 新規作成ボタン -->
                <button class="master-create button is-small" @click="createMaster()" v-if="isCreateMaster === false">
                  新規作成
                </button>
              </fieldset>
            </div>
            <!-- 編集中の表示 -->
            <div class="master-edit-area" v-else>
              <!-- 有効/無効 -->
              <button class="toggle-apply button is-small" :class="{'is-primary': isWorking === false}" @click="workingSwitch()">
                {{isWorking ? '無効にする' : '有効にする'}}
              </button>
              <!-- 設定保存ボタン -->
              <button class="master-edit button is-small is-primary" @click="saveMaster()">
                保存
              </button>
              <!-- 編集キャンセルボタン -->
              <button class="master-edit-cancel button is-small" @click="cancelEditMaster()">
                キャンセル
              </button>
              <!-- マスター削除ボタン (編集中以外は非活性) -->
              <button class="master-delete button is-small" @click="deleteMaster()" :disabled="isMasterDeletable() === false">
                マスター削除
              </button>
            </div>
            <!-- インポート/エクスポート -->
            <div class="data-in-out-area">
              <!-- インポートボタン -->
              <b-upload
                v-model="importFile"
                accept=".csv"
              >
                <!-- 未編集状態の時だけインポート可能 -->
                <a class="data-import button is-small" :disabled="isImportable() === false">
                  インポート
                </a>
              </b-upload>
              <!-- 未編集状態の時だけエクスポートボタン -->
              <button class="data-export button is-small" @click="dataExport()" :disabled="isExportable() === false">
                エクスポート
              </button>
              <!-- テンプレート項目選択遷移ボタン -->
              <button class="template-download button is-small" @click="templateDownload()" :disabled="isDownloadable() === false">
                テンプレート
              </button>
            </div>
            <!-- 自動連携再開 -->
            <div class="resume-area">
              <!-- 媒体連携と自動採用フローの再開ボタン -->
              <button
                :class="{
                  // 自動連携一時停止設定中の場合
                  'resume-alignment button is-small is-primary': isSuspend === true,
                  // 自動連携一時停止解除中の場合
                  'resume-alignment button is-small': isSuspend === false}"
                  @click="restartBatch()"
                  :disabled="isResumable() === false"
                >
                自動連携再開
              </button>
            </div>
            <div class="suspend-message">
              <!-- 自動連携一時停止中メッセージ -->
              <p v-if="isSuspend === true">以下が一時停止中です</p>
              <p v-if="taskSuspendFlag.media_cooperation === true">・媒体連携</p>
              <p v-if="taskSuspendFlag.auto_recruitment_flow_action === true">・自動採用フロー</p>
            </div>
            <!-- 閉じるボタン -->
            <div class="master-setting-close-area">
              <button class="master-setting-close button is-small" @click="modalClose()" :disabled="isClosable() === false">
                閉じる
              </button>
            </div>
          </div>
          <div v-else-if="isTemplateCreate() === true" class="master-edit-area">
            <!-- テンプレート項目選択キャンセルボタン -->
            <button class="master-edit-cancel button is-small" @click="cancelEditMaster()">
              キャンセル
            </button>
            <!-- テンプレートファイルダウンロード -->
            <button class="template-download button is-small" @click="templateDownload()">
              ダウンロード
            </button>
          </div>
        </div>
      </fieldset>
      <!-- 媒体連携項目マスターデータ -->
      <div
        ref="settingBody"
        :style="settingBodyHeight"
        :key="settingBodyHeight"
        class="master-setting-body disp-scroll"
      >
        <fieldset :disabled="isMasterEditing() === false || isResponseWaiting">
          <div class="master-setting-items">
            <div class="master-setting-item">
              <label>
                <input
                  v-model="currentMaster.use_unmapped_as_raw"
                  :disabled="isMasterEditing() === false || isResponseWaiting"
                  type="checkbox"
                />
                項目が該当しなかった場合(未設定)を非表示
              </label>
            </div>
            <div class="master-setting-item">
              <label>
                <input
                  v-model="currentMaster.use_partial_match"
                  :disabled="isMasterEditing() === false || isResponseWaiting"
                  type="checkbox"
                />
                一部一致検索を適用する
              </label>
            </div>
          </div>
          <!-- 項目紐付けテーブル -->
          <table
            v-if="currentMaster && currentMaster.common_item_data && currentMaster.media_item_data"
            class="item-table"
          >
            <!-- ヘッダ行 -->
            <thead>
              <tr>
                <!-- 行番号 -->
                <th
                  class="th-header-no is-small"
                >
                  No.
                </th>
                <th
                  class="th-header-add is-small"
                  v-if="isAddDelDisp() === true">
                  追加/削除
                </th>
                <!-- 共通項目列 -->
                <th
                  class="th-header-common" :class="{'th-header-1': isAddDelDisp() === false, 'th-header-2': isAddDelDisp() === true}"
                >
                  <!-- 共通項目プルダウン -->
                  <div class="table-cell common-name">
                    <p>共通項目名</p>
                  </div>
                  <div class="table-cell common-name">
                    <select
                      class="common-item-select"
                      v-model="currentMaster.common_item_data.header.applicant_column"
                    >
                      <option
                        v-for="(commonItem, commonItemIndex) in currentMaster.common_item_data.header.item_list"
                        :key="commonItem.item_name + '_' + commonItemIndex"
                        :value="commonItem.id"
                      >
                        {{ commonItem.item_name }}
                      </option>
                    </select>
                  </div>
                </th>
                <!-- 媒体列 -->
                <th
                  v-for="(mediaData, mediaIndex) in currentMaster.media_item_data"
                  :key="mediaData.header.media_name_short + '_' +  mediaIndex"
                  :class="{'th-header-3': isAddDelDisp() === false, 'th-header-4': isAddDelDisp() === true}"
                >
                  <div class="table-cell media-name">
                    <!-- 適用チェックボックス (媒体項目マスター設定を適用する媒体を選択するチェックボックス) -->
                    <input class="is-small" type="checkbox" v-model="mediaData.header.is_apply" />
                    <!-- 媒体名 -->
                    <p>{{ mediaData.header.media_name_short }}</p>
                  </div>
                  <!-- 媒体ログインID -->
                  <div class="table-cell media-name">
                    <p>{{ mediaData.header.company_recruit_account }}</p>
                  </div>
                  <!-- 媒体側項目プルダウン -->
                  <div class="table-cell media-name">
                    <select
                      class="common-item-select"
                      v-model="mediaData.header.media_column"
                      :disabled="!mediaData.header.item_list"
                    >
                      <option
                        v-for="(mediaItem, mediaItemIndex) in mediaData.header.item_list"
                        :key="mediaItem.item_name + '_' + mediaItemIndex"
                        :value="mediaItem.id"
                      >
                        {{ mediaItem.item_name }}
                      </option>
                    </select>
                  </div>
                </th>
              </tr>
            </thead>
            <!-- データ行 -->
            <tbody v-if="isShowData() === true">
              <!-- 各項目データ -->
              <tr
                v-for="(rowData, rowIndex) in currentMaster.common_item_data.data.slice((Number(currentPage) * Number(rowRange)) - Number(rowRange), (Number(currentPage) * Number(rowRange)))"
                :key="rowData.id + '_' + rowData.row_no"
              >
                <!-- 行番号 -->
                <td
                  class="td-item-no"
                  :class="{'data-row-even': rowIndex % 2 === 0, 'data-row-odd': rowIndex % 2 !== 0}"
                >
                  {{ rowData.row_no + 1 }}
                </td>
                <!-- 増減ボタン列 -->
                <td
                  class="td-item-add"
                  :class="{'data-row-even': rowIndex % 2 === 0, 'data-row-odd': rowIndex % 2 !== 0}"
                  v-if="isAddDelDisp() === true"
                >
                  <!-- 増減ボタン -->
                  <button
                    class="button fluctuation-button"
                    :enabled="rowAddOrDel"
                    @click="addItem(rowData.row_no + 1)"
                  >
                    <span class="mdi mdi-plus-box"></span>
                  </button>
                  <button
                    class="button fluctuation-button"
                    :enabled="rowAddOrDel"
                    @click="deleteItem(rowData.row_no)"
                  >
                    <span class="mdi mdi-minus-box"></span>
                  </button>
                </td>
                <!-- 共通項目列 -->
                <td
                  class="td-item-common"
                  :class="{'data-row-even': rowIndex % 2 === 0, 'data-row-odd': rowIndex % 2 !== 0, 'td-item-1': isMasterEditing() === false, 'td-item-2': isMasterEditing() === true}"
                >
                  <!-- 入力データ -->
                  <input class="table-cell" type="input" v-model="rowData.item_data" />
                </td>
                <!-- 媒体列 -->
                <td
                  :class="{'data-row-even': rowIndex % 2 === 0, 'data-row-odd': rowIndex % 2 !== 0, 'td-item-3': isMasterEditing() === false, 'td-item-4': isMasterEditing() === true}"
                  v-for="(inputData, inputDataIndex) in currentMaster.media_item_data"
                  :key="'mediaItemData_' + rowIndex + '_' + inputDataIndex"
                >
                  <!-- 入力データ -->
                  <input class="table-cell" type="input" v-model="inputData.data.slice((Number(currentPage) * Number(rowRange)) - Number(rowRange), (Number(currentPage) * Number(rowRange)))[rowIndex].item_data" />
                </td>
              </tr>
            </tbody>
          </table>
        </fieldset>
      </div>
      <fieldset :disabled="isResponseWaiting">
        <!-- ページネーション -->
        <div class="table-footer" v-if="loadCount">
          <b-select class="-left-8px" size="is-small" v-model="rowRange" @input="currentPage = 1">
            <option value="50" selected>50件表示</option>
            <option value="100">100件表示</option>
            <option value="150">150件表示</option>
            <option value="200">200件表示</option>
          </b-select>
          <b-pagination
            style="margin: 0px; align-items: flex-start; font-size: 14px; padding-left: 16px; padding-right: 8px;"
            :total="loadCount"
            :current.sync="currentPage"
            :per-page="rowRange">
            <b-pagination-button
              slot-scope="props"
              :page="props.page"
              tag="button">
              {{ props.page.number }}
            </b-pagination-button>
            <b-pagination-button
              slot="previous"
              slot-scope="props"
              :page="props.page"
              tag="button">
              ＜
            </b-pagination-button>
            <b-pagination-button
              slot="next"
              slot-scope="props"
              :page="props.page"
              tag="button">
                ＞
            </b-pagination-button>
          </b-pagination>
          <!-- 総件数と表示番号 -->
          <p style="padding-left: 16px;">行数：{{ loadCount }}　媒体数：{{ currentMaster.media_item_data.length }}</p>
        </div>
      </fieldset>
    </div>
  </ModalBase>
</template>

<script>
import { mapState } from 'vuex'
// モーダルベース
import ModalBase from '@/hrm-frontend/views/atomic/screen-components/basic-components/base-modal/ModalBase'
// 処理中メッセージモーダル
import LoadingModal from '@/hrm-frontend/views/atomic/screen-components/purpose_components/common-components/LoadingModal'
// アラートモーダル
import AlertModal from '@/hrm-frontend/views/atomic/screen-components/purpose_components/common-components/AlertModal'
// アラートモーダル用定義
import AlertDispFunctions from '@/hrm-frontend/modules/functions/alert-functions'
// ツールチップ
import Tooltip from '@/components/atoms/tooltips/Tooltip'
// フロントエンドログMixin
import FrontendLogMixin from '@/hrm-frontend/views/mixins/frontend-log-mixin'
// 操作ログMixin
import controlLogMixin from '@/mixins/controlLogMixin'
// プロトタイプ宣言追加メソッド
import '@/hrm-frontend/modules/functions/prototype-extends'

export default {
  name: 'MediaCooperationItemMasterSetting',
  componentName: 'MediaCooperationItemMasterSetting',
  components: {
    // モーダルベース
    ModalBase,
    // 処理中メッセージモーダル
    LoadingModal,
    // アラートモーダル
    AlertModal,
    // ツールチップ
    Tooltip,
  },
  mixins: [
    // フロントエンドログMixin
    FrontendLogMixin,
    // 操作ログMixin
    controlLogMixin
  ],
  props: {
    /**
     * ロボリスト
     * @type {Array}
     */
    roboList: {
      type: Array,
      required: true,
    },
    companyColumnSetting: {
      type: Array,
      required: true,
    }
  },
  data () {
    return {
      /**
       * ログタイトル
       * @type {String}
       */
      logTitle: '媒体情報設定',
      /**
       * モーダル表示フラグ
       * @type {Boolean}
       */
      isShowModal: false,
      /**
       * アラートモーダル表示フラグ
       * @type {Boolean}
       */
      isAlertOpen: false,
      /**
       * アラートモーダル表示データ
       * @type {Object}
       */
      alertData: {},
      /**
       * アラート表示メソッド
       * @type {Object}
       */
      alertDispMethod: '',
      /**
       * 媒体連携項目マスターデータ表の高さ
       * @type {String}
       */
      settingBodyHeight: 'height: calc(100vh - 180px);',
      /**
       * 選択中マスターID
       * @type {Number}
       */
      currentMasterId: null,
      /**
       * 変更前媒体連携項目マスター
       * @type {Object}
       */
      changeBeforeData: {},
      /**
       * 項目マスター表示順
       * @type {Number}
       */
      masterOrder: 0,
      /**
       * ページ番号
       * @type {Number}
       */
      currentPage: 1,
      /**
       * 1ページ表示件数初期値
       * @type {Number}
       */
      initialRowRange: 50,
      /**
       * 1ページ表示件数
       * @type {Number}
       */
      rowRange: 50,
      /**
       * マスター適用フラグ
       * @type {Boolean}
       */
      isWorking: true,
      /**
       * マスター名変更フラグ
       * @type {Boolean}
       */
      isEditName: false,
      /**
       * マスター新規作成フラグ
       * @type {Boolean}
       */
      isCreateMaster: false,
      /**
       * マスター編集フラグ
       * @type {Boolean}
       */
      isEditMaster: false,
      /**
       * テンプレート項目選択中フラグ
       * @type {Boolean}
       */
      isTemplateItemSelecting: false,
      /**
       * 一時停止中フラグ
       * @type {Boolean}
       */
      isSuspend: false,
      /**
       * マスター削除フラグ
       * @type {Boolean}
       */
      isMasterDeleting: false,
      /**
       * 紐づけ済みロボ一覧
       * @type {String}
       */
      linkedRoboList: '',
      /**
       * 設定名変更
       * @type {String}
       */
      masterName: '',
      /**
       * 選択中共通項目
       * @type {Number}
       */
      applicantColumn: 0,
      /**
       * 場対連携項目マスター名
       * @type {String}
       */
      masterName: '',
      /**
       * インポートファイル
       * @type {Object}
       */
      importFile: null,
      /**
       * 項目データ行追加/削除実行中フラグ
       * @type {Boolean}
       */
      rowAddOrDel: false,
      /**
       * 項目データ行追加/削除フラグ
       * @type {Boolean}
       */
      isAddDelFlag: false,
      /**
       * レスポンス待ちフラグ
       * @type {Boolean}
       */
      isResponseWaiting: false,
      /**
       * 処理中タイトル
       * @type {String}
       */
      loadingTitle: '処理中です...',
      /**
       * 処理中メッセージ
       * @type {String}
       */
      loadingMessage: '',
      /**
       * アラートモーダル呼び出し元メソッド名一覧
       * ※メソッド名を変更した場合は修正する事
       * @type {Object}
       */
      ALERT_CALL_METHOD_LIST: {
        // 自動連携一時停止
        LINK_SUSPEND: 'linkSuspend',
        // 自動連携再開
        LINK_RESUME: 'restartBatch',
        // 媒体連携項目マスター削除
        DELETE_MASTER: 'deleteMaster',
        // インポート用テンプレートダウンロード
        TEMPLATE_DOWNLOAD: 'templateDownload',
        // メッセージアラート
        MESSAGE_ALERT: 'dispMessageAlert',
        // エラーアラート
        ERROR_ALERT: 'dispErrorAlert',
        // ロボ紐付け確認
        CONFIRMED_ROBO_LINK: 'confirmedRoboLink',
      },
      /**
       * 自動連携を一時停止実行メソッド名
       * @type {String}
       */
      suspendMethodName: '',
      /**
       * 自動連携を一時停止するメソッド名一覧
       * ※メソッド名を変更した場合は修正する事
       * @type {Object}
       */
      SUSPEND_METHOD_LIST: {
        // 媒体連携項目マスター監視
        MASTER_WATCH: 'mediaCooperationItemMasterList',
        // 媒体連携項目マスター編集開始
        EDIT_MASTER: 'editMaster',
        // 媒体連携項目マスター新規作成開始
        CREATE_MASTER: 'createMaster',
        // 媒体連携項目マスター削除
        DELETE_MASTER: 'deleteMaster',
        // インポート
        CSV_IMPORT: 'dataImport',
      }
    }
  },
  computed: {
    // 媒体連携関連ストア
    ...mapState('recruitmentMedia', [
      // モーダル状態遷移
      'modalState',
      // 媒体連携項目マスター一覧
      'mediaCooperationItemMasterNameList',
      // 選択中媒体連携項目マスター
      'currentMaster',
      // 媒体連携項目マスターテンプレート
      'mediaCooperationItemMasterTemplate',
      // 項目データテンプレート
      'itemDataTemplate',
      // データ読み込み中フラグ
      'isDataLoading',
      // 読み込み件数
      'loadCount',
    ]),
    // バックグラウンド関連ストア
    ...mapState('background', [
      // バッチタスク一時停止フラグ
      'taskSuspendFlag',
    ]),
    // 項目マスター表示順
    // 加減算したthis.currentMaster.orderをv-modelで直接扱いたいのでgetterとsetterを設ける
    currentMasterOrder: {
      get () {
        // orderが0始まりなので1加算する
        return this.masterOrder + 1
      },
      set (value) {
        // orderが0始まりなので1減算する
        this.masterOrder = value - 1
      }
    },
  },
  mounted () {
    // ウィンドウリサイズイベント登録
    window.addEventListener('resize', this.resizeMasterTable)
    this.$nextTick(() => {
        this.resizeMasterTable()
      })
  },
  beforeDestroy () {
    // ウィンドウリサイズイベント破棄
    window.removeEventListener('resize', this.resizeMasterTable)
  },
  watch: {
    /**
     * 媒体連携一時停止フラグ監視監視
     */
    'taskSuspendFlag.media_cooperation' () {
      this.isSuspend = Boolean(this.taskSuspendFlag.media_cooperation || this.taskSuspendFlag.auto_recruitment_flow_action)
      // ヘッダ部分の高さが変わるので、DOM描画後に高さ調整を行う
      this.$nextTick(() => {
        this.resizeMasterTable()
      })
    },
    /**
     * 自動採用フロー一時停止フラグ監視監視
     */
    'taskSuspendFlag.auto_recruitment_flow_action' () {
      this.isSuspend = Boolean(this.taskSuspendFlag.media_cooperation || this.taskSuspendFlag.auto_recruitment_flow_action)
      // ヘッダ部分の高さが変わるので、DOM描画後に高さ調整を行う
      this.$nextTick(() => {
        this.resizeMasterTable()
      })
    },
    /**
     * モーダル状態遷移監視
     */
    async 'modalState.mediaCooperationItemMaster' () {
      this.sendSystemLog(
        this.LOG_LEVEL.OPERATION,
        '媒体連携項目マスター',
        '媒体連携項目マスター設定モーダルを開きました。'
      )
      let isError = false
      try {
        // 自モーダルの状態遷移を監視する
        this.isShowModal = this.modalState.mediaCooperationItemMaster
        if (this.isShowModal === true) {
          // モーダルを開いた時、DOM描画後に高さ調整を行う
          this.$nextTick(() => {
            this.resizeMasterTable()
          })

          this.loadingMessage = '媒体連携項目マスターを取得しています。'
          this.isResponseWaiting = true

          // バッチタスク一時停止フラグ取得
          this.$store.dispatch('background/getBatchTaskSuspendFlag')
            .catch((error) => {
              throw error
            })

          // 新規マスターテンプレート取得
          await this.$store.dispatch('recruitmentMedia/getMediaCooperationItemMasterTemplate')
            .catch((error) => {
              throw error
            })

          // 媒体連携項目マスター一覧取得
          await this.$store.dispatch('recruitmentMedia/getMediaCooperationItemMasterList')
            .catch((error) => {
              throw error
            })

          // 先頭のマスターを選択状態にする
          if (this.mediaCooperationItemMasterNameList.length > 0 && this.mediaCooperationItemMasterNameList[0].id) {
            this.currentMasterId = this.mediaCooperationItemMasterNameList[0].id
            if (this.currentMasterId) {
              // 項目マスターデータ取得
              await this.getMasterData()
            }
          }
        }
      } catch (error) {
        isError = true
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター状態遷移監視エラー',
          'モーダル状態遷移監視時に例外が発生しました。\n\n{0}'.format(error)
        )
        this.dispErrorAlert('媒体連携項目マスターの取得に失敗しました。')
      } finally {
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        this.dispInit()
        if (isError === true) {
          this.modalClose()
        }
      }
    },
    /**
     * 処理待ちフラグ監視
     */
    isResponseWaiting () {
      try {
        // モーダルを開いた時、DOM描画後に高さ調整を行う
        this.$nextTick(() => {
          this.resizeMasterTable()
        })
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター処理待ちフラグ監視エラー',
          '処理待ちフラグ監視時に例外が発生しました。\n\n{0}'.format(error)
        )
      }
    },
    /**
     * インポートファイルアップロード監視
     */
    importFile () {
      try {
        if (this.importFile) {
          // CSVインポート実行
          this.dataImport()
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターインポートファイルアップロード監視エラー',
          'インポートファイルアップロード監視時に例外が発生しました。\n\n{0}'.format(error)
        )
      }
    },
  },
  methods: {
    /**
     * リサイズ処理
     */
    resizeMasterTable () {
      try {
        if (this.$refs.settingHeader && this.$refs.settingBody) {
          // 要素が描画済みなら高さ調整を行う
          const settingHeaderHeight = this.$refs.settingHeader.clientHeight
          this.settingBodyHeight = this.$refs.settingBody.clientHeight
          if (settingHeaderHeight >= 62) {
            // 媒体連携項目マスター操作部分の要素の高さが62pxを超えたら、表の高さを調整する
            this.settingBodyHeight = 'height: calc(100vh - {0}px);'.format(180 + settingHeaderHeight)
          }
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターモーダルリサイズエラー',
          'モーダルリサイズ時に例外が発生しました。\n\n{0}'.format(error)
        )
      }
    },
    /**
     * モーダルクローズ
     */
    modalClose () {
      try {
        // 一時停止中であれば解除する
        if (this.isSuspend === true) {
          this.linkResume()
        }
        // 編集後処理
        this.$store.dispatch('recruitmentMedia/editTerminate', this.currentMasterId)
        // 表示初期化
        this.dispInit()
        // 媒体連携項目マスター設定モーダルを閉じる
        this.$store.dispatch('recruitmentMedia/closeModal', 'mediaCooperationItemMaster')
        // 空のマスターデータをカレントにする
        this.$store.dispatch('recruitmentMedia/setCurrentMaster', this.copyMediaCooperationItemMasterDataTemplate())
        this.masterName = ''
        this.rowRange = this.initialRowRange
        this.sendSystemLog(
          this.LOG_LEVEL.OPERATION,
          '媒体連携項目マスター',
          '媒体連携項目マスター設定モーダルを閉じました。'
        )
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターモーダルクローズエラー',
          'モーダルクローズ時に例外が発生しました。\n\n{0}'.format(error)
        )
      }
    },
    /**
     * 操作パネル表示フラグ
     * @return {Boolean} 操作パネル表示判定結果
     */
    isPanelDisp () {
      return Boolean(this.isEditName === false && this.isTemplateItemSelecting === false)
    },
    /**
     * マスター名編集中判定
     * 以下のいずれかの場合はマスター名編集中
     * ・マスター名編集
     * ・マスターデータ編集
     * ・マスター新規作成
     * ・インポート用テンプレート作成
     * @return {Boolean} マスター名編集中判定結果
     */
    isNameEditing () {
      return Boolean(
        this.isEditName === true ||
        this.isEditMaster === true ||
        this.isCreateMaster === true ||
        this.isTemplateItemSelecting === true
      )
    },
    /**
     * マスター編集中判定
     * 以下のいずれかの場合はマスターデータ編集中
     * ・マスターデータ編集
     * ・マスター新規作成
     * ・インポート用テンプレート作成
     * @return {Boolean} マスター編集中判定結果
     */
    isMasterEditing () {
      return Boolean((this.isEditMaster === true || this.isCreateMaster === true) && this.isTemplateItemSelecting === false)
    },
    /**
     * 追加/削除欄表示判定
     * 以下のいずれかの場合はでインポート用テンプレート作成中ではない場合はマスターデータ編集中
     * ・マスターデータ編集
     * ・マスター新規作成
     * ・インポート用テンプレート作成中
     * @return {Boolean} 削除欄表示判定結果
     */
    isAddDelDisp () {
      return Boolean((this.isEditMaster === true || this.isCreateMaster === true) && this.isTemplateItemSelecting === false)
    },
    /**
     * データ行表示判定
     * インポート用テンプレート作成でなければ表示
     * @return {Boolean} データ行表示判定結果
     */
    isShowData () {
      return Boolean(this.isTemplateItemSelecting === false)
    },
    /**
     * マスター新規作成中判定
     * @return {Boolean} マスター新規作成中判定結果
     */
    isMasterCreating () {
      return Boolean(this.isCreateMaster === true)
    },
    /**
     * 操作パネル表示判定
     * マスター名編集中とインポート用テンプレート作成時は操作パネルを非表示
     * @return {Boolean} パネル表示判定結果
     */
    isShowOperationPanel () {
      return Boolean(this.isEditName === false && this.isTemplateItemSelecting === false)
    },
    /**
     * マスター削除ボタン有効判定
     * マスターデータ編集中で新規作成時およびテンプレート項目選択中ではない場合に削除可能
     * @return {Boolean} 削除ボタン判定結果
     */
    isMasterDeletable () {
      return Boolean(this.isEditMaster === true && this.isCreateMaster === false && this.isTemplateItemSelecting === false)
    },
    /**
     * 自動連携再開ボタン有効判定
     * 編集中でない場合でマスター編集を保存または新規作成して保存した後に有効
     * @return {Boolean} 自動連係再開ボタン有効判定結果
     */
    isResumable () {
      return Boolean(this.isMasterEditing() === false && this.isSuspend === true)
    },
    /**
     * 初回新規作成判定
     * 項目マスターが1件しかない時の新規作成状態は初回新規作成
     * @return {Boolean} 初回新規作成判定時結果
     */
    isFirstCreating () {
      return Boolean(this.isMasterCreating() === true && this.mediaCooperationItemMasterNameList.length === 1)
    },
    /**
     * 編集キャンセル可能判定
     * 初回新規作成時は編集キャンセル不可
     * @return {Boolean} 編集キャンセル可能判定結果
     */
    isEditCancelable () {
      return Boolean(this.isFirstCreating() === false)
    },
    /**
     * インポート可能判定
     * 編集時と新規作成時、テンプレート項目選択時はインポート不可
     * @return {Boolean} インポート可能判定結果
     */
    isImportable () {
      return Boolean(this.isMasterEditing() === false && this.isTemplateItemSelecting === false)
    },
    /**
     * エクスポート可能判定
     * 編集時と新規作成時、テンプレート項目選択時はエクスポート不可
     * @return {Boolean} エクスポート可能判定結果
     */
    isExportable () {
      return Boolean(this.isMasterEditing() === false && this.isTemplateItemSelecting === false && this.mediaCooperationItemMasterNameList.length > 0)
    },
    /**
     * インポート用テンプレート作成中判定
     * インポート用テンプレート作成中の操作パネル表示
     * @return {Boolean} インポート用テンプレート作成中判定結果
     */
    isTemplateCreate () {
      return this.isTemplateItemSelecting
    },
    /**
     * テンプレートダウンロード可能判定
     * 編集時および新規作成時はダウンロード不可
     * @return {Boolean} テンプレートダウンロード可能判定結果
     */
    isDownloadable () {
      return Boolean(this.isMasterEditing() === false)
    },
    /**
     * 閉じるボタン有効判定
     * @return {Boolean} 閉じるボタン有効判定結果
     */
    isClosable () {
      // 未編集時にクローズ可
      return Boolean(this.isMasterEditing() === false)
    },
    /**
     * 表示用紐付け済みロボ一覧作成
     */
    makeLinkedRobo () {
      if (this.currentMaster.master_id) {
        // 選択中マスターに紐づくロボを取得
        const linkedRobo = this.roboList.filter(robo => {
            return robo.item_master !== null && robo.item_master === this.currentMaster.master_id
          }
        )
        // 紐づけられているロボ名一覧を作成
        let roboNameList = []
        for (let robo of linkedRobo) {
          roboNameList.push('・{0}'.format(robo.name))
        }
        if (roboNameList.length > 0) {
          this.linkedRoboList = roboNameList.join('\n')
        } else {
          this.linkedRoboList = ''
        }
      } else {
        this.linkedRoboList = ''
      }
    },
    /**
     * 表示用紐付け済みロボ一覧表示
     */
    confirmedRoboLink () {
      try {
        const message = '以下のロボに紐づいています。\n\n{0}'.format(this.linkedRoboList)
        // アラートモーダル表示設定
        this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.CONFIRMED_ROBO_LINK
        this.alertData = AlertDispFunctions.createAlertData(
          '紐付け中ロボ確認',
          message,
          AlertDispFunctions.ALERT_DEF.ICON_TYPE.DEFAULT,
          AlertDispFunctions.ALERT_DEF.DISP_TYPE.ALERT,
          this.$options._componentTag,
          this.ALERT_CALL_METHOD_LIST.CONFIRMED_ROBO_LINK,
          null,
          null
        )
        // アラートモーダル表示
        this.isAlertOpen = true
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターアラートモーダル表示エラー',
          'アラートモーダル表示時に例外が発生しました。\n表示内容：{0}\n\n{1}'.format(message, error)
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
      }
    },
    /**
     * 項目マスター表示順
     * @return {String} 項目マスター表示順
     */
    masterOrderList () {
      let orderList = []
      this.mediaCooperationItemMasterNameList.forEach(master => {
        if (master.id) {
          orderList.push('{0}: {1}'.format(Number(master.order) + 1, master.master_name))
        }
      })
      if (orderList.length === 0) {
        if (this.masterName) {
          orderList.push('1: {0}'.format(this.masterName))
        } else {
          orderList.push('1: マスター未登録')
        }
      }
      return orderList.join('\n')
    },
    /**
     * 表示順の最大値制御
     */
    changeOrderNo () {
      // 範囲を超えて入力されたら範囲内に収める
      if (this.masterOrder > this.mediaCooperationItemMasterNameList.length - 1) {
        this.masterOrder = this.mediaCooperationItemMasterNameList.length - 1
      } else if (this.masterOrder < 0) {
        this.masterOrder = 0
      }
    },
    /**
     * 媒体連携項目マスターテンプレートコピー
     * そのまま使うと参照ごとコピーしてしまうので、ディープコピーする
     * @return {Object} 媒体連携項目マスターテンプレート
     */
    copyMediaCooperationItemMasterTemplate () {
      try {
        let template = JSON.parse(JSON.stringify(this.mediaCooperationItemMasterTemplate))
        return template
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターテンプレートコピーエラー',
          '媒体連携項目マスターテンプレートコピー時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 媒体連携項目マスターデータテンプレートコピー
     * そのまま使うと参照ごとコピーしてしまうので、ディープコピーする
     * @return {Object} 媒体連携項目マスターテンプレート
     */
    copyMediaCooperationItemMasterDataTemplate () {
      try {
        return JSON.parse(JSON.stringify(this.mediaCooperationItemMasterTemplate))
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターテンプレートコピーエラー',
          '媒体連携項目マスターテンプレートコピー時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 項目マスター表示変更
     */
    async masterSelect () {
      // 項目マスターデータ取得
      if (this.currentMasterId) {
        try {
          this.loadingMessage = '媒体連携項目マスターを取得しています。'
          this.isResponseWaiting = true

          // 項目マスターデータ取得
          await this.getMasterData()
        } catch (error) {
          // エラーログ登録
          this.sendSystemLog(
            this.LOG_LEVEL.ERROR,
            '媒体連携項目マスター表示切替エラー',
            '媒体連携項目マスター(ID: {0})表示切替時に例外が発生しました。\n\n{1}'.format(this.currentMasterId, error)
          )
          this.dispErrorAlert('項目マスター表示切替に失敗しました。')
        } finally {
          this.dispInit()
          this.isResponseWaiting = false
        }
      } else {
        // 空のマスターデータをカレントにする
        this.$store.dispatch('recruitmentMedia/setCurrentMaster', this.copyMediaCooperationItemMasterDataTemplate())
      }
    },
    /**
     * 媒体連携項目マスターデータ取得
     */
    async getMasterData () {
      //  取得件数
      const fetchCount = 5000

      // 1ページ目を取得
      // 表示中の媒体連携項目マスターをクリアする
      this.$store.dispatch('recruitmentMedia/clearCurrentMaster')
      await this.$store.dispatch('recruitmentMedia/getMediaCooperationItemMaster', {master_id: this.currentMasterId, current_page: 1, row_range: fetchCount})
        .catch((error) => {
          throw error
        })
      // 最大ページ番号を算出
      const maxPageNo = Math.ceil(this.currentMaster.data_row_count / fetchCount)
      // 2ページ目以降があれば、最終ページまで1ページずつデータを取得
      for (let index = 2; index <= maxPageNo; index++) {
        this.loadingMessage = '媒体連携項目マスターを取得しています。<br>({0}/{1}行)'.format((index - 1) * fetchCount + 1, this.currentMaster.data_row_count)
        await this.$store.dispatch('recruitmentMedia/getMediaCooperationItemMaster', {master_id: this.currentMasterId, current_page: index, row_range: fetchCount})
          .catch((error) => {
            throw error
          })
      }
      this.currentMasterId = this.currentMaster.master_id
    },
    /**
     * 編集状態遷移時初期化
     */
    editInit () {
      try {
        // 編集前データ退避
        this.changeBeforeData = JSON.parse(JSON.stringify(this.currentMaster))
        // マスター表示順反映
        this.masterOrder = this.currentMaster.order
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター表示状態初期化処理エラー',
          '表示状態初期化処理時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 表示状態初期化
     */
    dispInit () {
      try {
        if (this.currentMaster) {
          if (this.mediaCooperationItemMasterNameList.length === 0) {
            // 項目マスターリストがない場合
            this.currentMasterId = null
          } else {
            if (this.currentMaster.master_id) {
              // 表示中の項目マスターIDを保持する
              this.currentMasterId = this.currentMaster.master_id
            } else {
              if (this.isCreateMaster === true) {
                // 新規作成やインポート直後はnullをセット
                this.currentMasterId = null
              } else {
                // 項目マスター削除後など、選択中マスターが空の場合は先頭のマスターを取得する
                this.currentMasterId = this.mediaCooperationItemMasterNameList[0].id
              }
            }
          }
          // 表示順反映
          this.masterOrder = this.currentMaster.order
          // 共通項目初期化
          this.applicantColumn = this.currentMaster.common_item_data.header.applicant_column
          // マスター名の入力ボックスを初期化する
          this.masterName = this.currentMaster.master_name
          // マスター名表示状態初期化
          this.isEditName = false
          // マスター新規作成フラグ初期化
          this.isCreateMaster = false
          // マスター編集フラグ初期化
          this.isEditMaster = false
          // テンプレート作成状態初期化
          this.isTemplateItemSelecting = false
          // 処理中モーダルクローズ
          this.isResponseWaiting = false
          // 編集前データ初期化
          this.changeBeforeData = JSON.parse(JSON.stringify(this.currentMaster))
          // アップロードファイル初期化
          this.importFile = null
          // 紐付け中ロボ一覧作成
          this.makeLinkedRobo()
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター編集状態終了処理エラー',
          '編集状態終了処理時に例外が発生しました。\n選択中マスター：{0}\n\n1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 媒体連携項目マスター名編集遷移
     */
    editName () {
      this.sendSystemLog(
        this.LOG_LEVEL.OPERATION,
        '媒体連携項目マスター名編集',
        '媒体連携項目マスター名変更を開始しました。'
      )
      try {
        // 編集状態遷移時初期化
        this.editInit()
        // 設定名変更状態に遷移する
        this.isEditName = true
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター名変更状態遷移エラー',
          '媒体連携項目マスター名変更状態遷移時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 媒体連携項目マスター名編集キャンセル
     */
    editNameCancel () {
      this.sendSystemLog(
        this.LOG_LEVEL.OPERATION,
        '媒体連携項目マスター名編集',
        '媒体連携項目マスター名変更をキャンセルしました。'
      )
      this.masterName = this.currentMaster.master_name
      this.isEditName = false
    },
    /**
     * 媒体連携項目マスター名保存
     */
    async saveName () {
      try {
        if (!this.masterName) {
          // マスター名がブランク
          this.dispErrorAlert('マスター名が入力されていません。')
          return
        }
        // マスター名保存API
        this.loadingMessage = 'マスター名を保存しています。'
        this.isResponseWaiting = true
        this.currentMaster.master_name = this.masterName
        this.currentMaster.order = this.masterOrder
        await this.$store.dispatch('recruitmentMedia/updateMediaCooperationItemMasterSort', this.currentMaster)
        await this.$store.dispatch('recruitmentMedia/saveMediaCooperationItemMasterName', this.mediaCooperationItemMasterNameList)
          .then(() => {
            // 操作ログ登録
            this.sendSystemLog(
              this.LOG_LEVEL.OPERATION,
              '媒体連携項目マスター名更新',
              'マスター名を「{0}」から「{1}」に更新しました。'.format(this.changeBeforeData.master_name, this.masterName)
            )
          })
          .catch((error) => {
            // エラーログ登録
            this.sendSystemLog(
              this.LOG_LEVEL.ERROR,
              '媒体連携項目マスター名更新エラー',
              'マスター名更新リクエスト時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
            )
            this.dispErrorAlert('マスター名の保存に失敗しました。')
          })
          .finally(() => {
            // 処理中モーダルクローズ
            this.isResponseWaiting = false
          })
        this.dispInit()
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター名更新エラー',
          'マスター名更新で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        this.dispErrorAlert('マスター名の保存に失敗しました。')
      }
    },
    /**
     * 媒体連携項目マスター適用操作
     */
    workingSwitch () {
      try {
        this.isWorking = !this.isWorking
        this.sendSystemLog(
          this.LOG_LEVEL.OPERATION,
          '媒体連携項目マスター有効化',
          '媒体連携項目マスターを{0}しました。'.format(this.isWorking ? '有効化' : '無効化')
        )
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター適用操作エラー',
          '媒体連携項目マスター適用操作で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 媒体連携および自動採用フロー一時停止
     * @param {String} suspendMethodName 一時停止を行ったメソッド名
     * @return {Object} 処理結果
     */
    linkSuspend (suspendMethodName) {
      try {
        // 一時停止実行メソッド名退避
        this.suspendMethodName = suspendMethodName

        // 一時停止されていなければ一時停止処理を行う
        if (this.isSuspend === false) {
          // 確認モーダル表示設定
          this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.LINK_SUSPEND
          this.alertData = AlertDispFunctions.createAlertData(
            '自動連携一時停止',
            '実行する前に媒体連携および自動採用フローを一時停止します。\nよろしいですか？\n\n※一時停止後に再開しなかった場合、1時間後に自動で再開されます。',
            AlertDispFunctions.ALERT_DEF.ICON_TYPE.WARNING,
            AlertDispFunctions.ALERT_DEF.DISP_TYPE.CONFIRM,
            this.$options._componentTag,
            this.ALERT_CALL_METHOD_LIST.LINK_SUSPEND,
            null,
            null
          )
          // 確認モーダル表示
          this.isAlertOpen = true
        } else {
          // 一時停止中の場合は後続処理を実行する
          this.linkSuspendSubsequent(AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES)
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター自動連携一時停止エラー',
          '自動連携一時停止で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
        this.dispErrorAlert('媒体連携および自動採用フローの停止に失敗しました。')
      }
    },
    /**
     * 自動連携一時停止後後続処理
     * @param {String} confirmResult 確認結果
     */
    linkSuspendSubsequent (confirmResult) {
      // 「はい」押下時はtrue、「いいえ」押下時はfalse
      const result = confirmResult === AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES ? true : false
      // 自動連携一時停止を行ったメソッドの後続処理を行う
      if (this.suspendMethodName === this.SUSPEND_METHOD_LIST.EDIT_MASTER) {
        // 媒体連携項目マスター編集開始後続処理
        this.editMaster(true, result)
      } else if (this.suspendMethodName === this.SUSPEND_METHOD_LIST.CREATE_MASTER) {
        // 媒体連携項目マスター編集開始後続処理
        this.createMaster(true, result)
      } else if (this.suspendMethodName === this.SUSPEND_METHOD_LIST.DELETE_MASTER) {
        // 媒体連携項目マスター削除後続処理
        this.deleteMaster(true, result)
      } else if (this.suspendMethodName === this.SUSPEND_METHOD_LIST.CSV_IMPORT) {
        // 媒体連携項目マスターインポート後続処理
        this.dataImport(true, result)
      }
      this.suspendMethodName = ''
    },
    /**
     * 媒体連携および自動採用フロー再開
     */
    linkResume () {
      try {
        // 一時停止されていれば一時停止解除処理を行う
        if (this.isSuspend === true) {
          // 媒体連携および自動採用フロー一時停止API
          this.loadingMessage = '媒体連携および自動採用フローを再開しています。'
          this.isResponseWaiting = true
          this.$store.dispatch('background/batchTaskResume', this.$options._componentTag)
            .catch((error) => {
              // エラーログ登録
              this.sendSystemLog(
                this.LOG_LEVEL.ERROR,
                '媒体連携項目マスター自動連携再開エラー',
                '自動連携再開リクエスト時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
              )
              this.dispErrorAlert('媒体連携および自動採用フローの再開に失敗しました。')
            })
            .finally(() => {
              // 処理中モーダルクローズ
              this.isResponseWaiting = false
            })
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター自動連携再開エラー',
          '自動連携再開で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        this.dispErrorAlert('媒体連携および自動採用フローの再開に失敗しました。')
      }
    },
    /**
     * 媒体連携項目マスターデータ編集遷移
     * @param {Boolean} isSubsequent 後続処理フラグ (false：初回処理、true：後続処理)
     * @param {Boolean} confirmResult 確認結果 (false：いいえ押下、true：はい押下)
     */
    editMaster (isSubsequent=false, confirmResult=false) {
      try {
        if (isSubsequent === false) {
          // 初回処理
          this.linkSuspend(this.SUSPEND_METHOD_LIST.EDIT_MASTER)
        } else {
          // 一時停止確認モーダル後の後続処理
          if (confirmResult === true) {
            // 操作ログ登録
            this.sendSystemLog(
              this.LOG_LEVEL.OPERATION,
              '媒体連携項目マスター編集状態遷移',
              'マスター編集状態に遷移しました。\n選択中マスター：{0}'.format(this.currentMaster.master_name)
            )
            // 編集状態遷移時初期化
            this.editInit()
            // 設定編集状態に遷移する
            this.isEditMaster = true
          }
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター編集状態遷移エラー',
          '編集状態遷移で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 媒体連携項目マスター編集キャンセル
     */
    cancelEditMaster () {
      try {
        // 操作ログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.OPERATION,
          '媒体連携項目マスター編集状態遷移',
          'マスター編集をキャンセルしました。\n選択中マスター：{0}'.format(this.currentMaster.master_name)
        )
        // 行の追加/削除の取り消し
        if (this.rowAddOrDel === true) {
          this.$store.dispatch('recruitmentMedia/editCancel', this.currentMasterId)
        }
        // データを編集前に戻す
        if (this.isCreateMaster === true) {
          // 新規作成状態の場合は最後尾に追加したテンプレートを削除する
          this.mediaCooperationItemMasterNameList.splice(this.mediaCooperationItemMasterNameList.length - 1, 1)
        }
        // 編集状態の場合は編集状態遷移前のデータを元に戻す
        this.$store.dispatch('recruitmentMedia/setCurrentMaster', JSON.parse(JSON.stringify(this.changeBeforeData)))
        // 自動連携再開
        this.linkResume()
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター編集状態キャンセルエラー',
          '編集状態キャンセルで例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      } finally {
        this.dispInit()
      }
    },
    /**
     * 媒体連携項目マスター新規作成
     * @param {Boolean} isSubsequent 後続処理フラグ (false：初回処理、true：後続処理)
     * @param {Boolean} confirmResult 確認結果 (false：いいえ押下、true：はい押下)
     */
    createMaster (isSubsequent=false, confirmResult=false) {
      // 操作ログ登録
      this.sendSystemLog(
        this.LOG_LEVEL.OPERATION,
        '媒体連携項目マスター編集状態遷移',
        'マスター新規作成状態に遷移しました。\n選択中マスター：{0}'.format(this.currentMaster.master_name)
      )
      try {
        if (isSubsequent === false) {
          // 連携処理を一時停止する
          this.linkSuspend(this.SUSPEND_METHOD_LIST.CREATE_MASTER)
        } else {
          // 一時停止確認モーダル後の後続処理
          if (confirmResult === true) {
            // 表示初期化
            // マスター新規作成の場合は先に初期化する
            this.dispInit()
            // 空のマスター情報を追加する
            this.mediaCooperationItemMasterNameList.push(this.copyMediaCooperationItemMasterTemplate())
            // 空のマスターデータをカレントにする
            this.$store.dispatch('recruitmentMedia/setCurrentMaster', this.copyMediaCooperationItemMasterDataTemplate())
            this.masterName = ''
            this.masterOrder = this.mediaCooperationItemMasterNameList.length - 1

            // 新規作成状態に遷移する
            this.isCreateMaster = true
          }
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター新規作成状態遷移エラー',
          '新規作成状態遷移で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 媒体連携項目マスターの更新または新規作成後の共通処理
     * @param {Boolean} isError 新規作成または保存でのエラーフラグ
     */
    saveFinish (isError=false) {
      try {
        if (isError === true && this.rowAddOrDel === true) {
          // 編集キャンセル
          this.$store.dispatch('recruitmentMedia/editCancel', this.currentMasterId)
        } else {
          // 編集後後処理
          this.$store.dispatch('recruitmentMedia/editTerminate', this.currentMasterId)
        }
        // 自動連携再開
        this.linkResume()
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター保存後処理エラー',
          '保存後処理で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 媒体連携項目マスター編集内容保存
     */
    async saveMaster () {
      let isCheckError = false
      try {
        // マスター名反映
        this.currentMaster.master_name = this.masterName
        // マスター表示順反映
        this.currentMaster.order = this.masterOrder
        // 有効状態反映
        this.currentMaster.isWorking = this.isWorking
        if (this.isCreateMaster === true || !this.currentMaster.master_id) {
          // 媒体連携項目マスター新規作成API
          this.loadingMessage = 'マスター設定を作成しています。'
          // 処理中モーダルオープン
          this.isResponseWaiting = true
          await this.$store.dispatch('recruitmentMedia/createMediaCooperationItemMaster', this.currentMaster)
            .then(result => {
              // resultがある場合はデータチェックエラー
              if (result && result.isSuccess === false) {
                if (result.isUserError === true) {
                  this.dispErrorAlert('媒体連携項目マスターの保存時に、以下のエラーを検出しました。\n\n{0}'.format(result.errorMessage.join('\n')))
                  isCheckError = true
                }
                throw error
              }
              this.currentMasterId = this.currentMaster.master_id
            })
            .catch((error) => {
              throw error
            })

          // 媒体連携項目マスター一覧再取得
          await this.$store.dispatch('recruitmentMedia/getMediaCooperationItemMasterList')
            .catch((error) => {
              throw error
            })
          // 項目マスターデータ取得しなおし
          await this.getMasterData()

          // 操作ログ登録
          this.sendSystemLog(
            this.LOG_LEVEL.OPERATION,
            '媒体連携項目マスター新規作成',
            'マスターを新規作成しました。\n選択中マスター：{0}'.format(this.currentMaster.master_name)
          )
        } else {
          // 媒体連携項目マスター保存API
          // データ数が閾値を超える場合は分割して保存する
          // ※データ数が多いと1回のAPIリクエストでタイムアウトが発生してしまう
          const saveThreshold = 2500
          const totalDataCount = this.currentMaster.data_row_count * this.currentMaster.media_item_data.length
          // 分割保存回数
          const saveCount = Math.ceil(totalDataCount / saveThreshold)
          // 1回の処理で保存する行数 (保存データ数は行数×媒体数＋共通項目)
          const singleSaveRowCount = Math.ceil(this.currentMaster.data_row_count / saveCount)
          let saveMasterData = JSON.parse(JSON.stringify(this.currentMaster))
          let startRow = 0
          let endRow = 0
          this.loadingMessage = 'マスター設定を保存しています。(1/{0})'.format(saveCount)
          this.isResponseWaiting = true
          // 分割保存処理
          for (let count = 0; count < saveCount; count++) {
            startRow = count * singleSaveRowCount
            endRow = startRow + singleSaveRowCount
            // 保存する分だけを格納する
            saveMasterData.common_item_data.data = this.currentMaster.common_item_data.data.slice(startRow, endRow)
            this.currentMaster.media_item_data.forEach((item_data, index) => {
              saveMasterData.media_item_data[index].data = item_data.data.slice(startRow, endRow)
            })
            // 分割分保存
            this.loadingMessage = 'マスター設定を保存しています。({0}/{1})'.format(count + 1, saveCount)
            await this.$store.dispatch('recruitmentMedia/saveMediaCooperationItemMaster', saveMasterData)
              .then(result => {
                // resultがある場合はデータチェックエラー
                if (result && result.isSuccess === false) {
                  if (result.isUserError === true) {
                    this.dispErrorAlert('媒体連携項目マスターの保存時に、以下のエラーを検出しました。\n\n{0}'.format(result.userErrorMessage.join('\n')))
                    isCheckError = true
                  }
                  throw result
                }
              })
              .catch((error) => {
                isCheckError = true
                throw error
              })
          }
          this.isResponseWaiting = false
          this.currentMasterId = this.currentMaster.master_id

          // 操作ログ登録
          this.sendSystemLog(
            this.LOG_LEVEL.OPERATION,
            '媒体連携項目マスター編集保存',
            '編集したマスターを保存しました。\n選択中マスター：{0}'.format(this.currentMaster.master_name)
          )
        }
        this.saveFinish()
        this.dispInit()
      } catch (error) {
        // resultがある場合はデータチェックエラー
        if (error.isUserError && error.isUserError === true) {
            this.dispErrorAlert('媒体連携項目マスターの保存時に、以下のエラーを検出しました。\n\n{0}'.format(error.userErrorMessage.join('\n')))
            isCheckError = true
        }
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター編集または新規作成保存エラー',
          '編集または新規作成で例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
        if(isCheckError === true) {
          this.dispErrorAlert('媒体連携項目マスターの保存に失敗しました。')
        }
        this.saveFinish(true)
      }
    },
    /**
     * 選択中媒体連携項目マスター削除
     * @param {Boolean} isSubsequent 後続処理フラグ (false：初回処理、true：後続処理)
     * @param {Boolean} confirmResult 確認結果 (false：いいえ押下、true：はい押下)
     */
    async deleteMaster (isSubsequent=false, confirmResult=false) {
      try {
        if (isSubsequent === false) {
          // ミニロボを持つ親ロボの紐づけ状態チェック
          if (this.linkedRoboList !== '') {
            this.dispErrorAlert('以下のロボに紐づけられているため、削除できません。\n削除する場合は紐付けの解除を行ってください。\n\n{0}'.format(this.linkedRoboList))
            return
          }
          // 確認モーダル表示設定
          this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.DELETE_MASTER
          this.alertData = AlertDispFunctions.createAlertData(
            '媒体連携項目マスター削除',
            '{0}を削除します。\nよろしいですか？'.format(this.currentMaster.master_name),
            AlertDispFunctions.ALERT_DEF.ICON_TYPE.DANGER,
            AlertDispFunctions.ALERT_DEF.DISP_TYPE.CONFIRM,
            this.$options._componentTag,
            this.ALERT_CALL_METHOD_LIST.DELETE_MASTER,
            null,
            null
          )
          // 確認モーダル表示
          this.isAlertOpen = true
        } else {
          if (confirmResult === true) {
            // 媒体連携項目マスターの削除
            this.loadingMessage = 'マスター設定を削除しています。'
            // 処理中モーダルオープン
            this.isResponseWaiting = true
            // 削除中状態に遷移
            this.isMasterDeleting = true
            await this.$store.dispatch('recruitmentMedia/deleteMediaCooperationItemMaster', this.currentMaster.master_id)
              .then(() => {
                // 操作ログ登録
                this.sendSystemLog(
                  this.LOG_LEVEL.OPERATION,
                  '媒体連携項目マスター削除',
                  'マスターを削除しました。\n選択中マスター：{0}'.format(this.currentMaster.master_name)
                )
                this.linkResume()
                this.dispInit()
                this.masterSelect()
              })
              .catch((error) => {
                // エラーログ登録
                this.sendSystemLog(
                  this.LOG_LEVEL.ERROR,
                  '媒体連携項目マスター削除エラー',
                  'マスター削除リクエスト時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
                )
                this.dispErrorAlert('マスター設定の削除に失敗しました。')
              })
              .finally(() => {
                // 削除中状態解除
                this.isMasterDeleting = false
                // 処理中モーダルクローズ
                this.isResponseWaiting = false
              })
          }
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター削除エラー',
          'マスター削除時に例外が発生しました。\n選択中マスター：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
        // 削除中状態解除
        this.isMasterDeleting = false
        this.dispErrorAlert('マスター設定の削除に失敗しました。')
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
      }
    },
    /**
     * インポート
     * 未編集時にインポートする
     * @param {Boolean} isSubsequent 後続処理フラグ (false：初回処理、true：後続処理)
     * @param {Boolean} confirmResult 確認結果 (false：いいえ押下、true：はい押下)
     */
    async dataImport (isSubsequent=false, confirmResult=false) {
      try {
        if (isSubsequent === false) {
          this.linkSuspend(this.SUSPEND_METHOD_LIST.CSV_IMPORT)
        } else {
          if (confirmResult === true) {
            let reader = new FileReader()
            reader.onload = async () => {
              this.loadingMessage = '{0}をインポートしています。'.format(this.importFile.name)
              // 処理中モーダルオープン
              // ※this.isResponseWaitingはsaveMaster()側でfalseにする
              this.isResponseWaiting = true
              // 空のマスター情報を追加する
              this.mediaCooperationItemMasterNameList.push(this.copyMediaCooperationItemMasterTemplate())
              // インポート実行
              await this.$store.dispatch('recruitmentMedia/importMediaCooperationItemMaster', this.importFile)
                .then(() => {
                  // 未保存データを表示するため、新規作成と同じ状態にする
                  this.masterName = this.currentMaster.master_name
                  this.masterOrder = this.mediaCooperationItemMasterNameList.length - 1
                  this.isCreateMaster = true
                  const message = 'マスターのインポートを行いました。\nマスター名：{0}\n引き続きインポートしたマスターの保存を行います。'.format(this.currentMaster.master_name)
                  // 操作ログ登録
                  this.sendSystemLog(
                    this.LOG_LEVEL.OPERATION,
                    '媒体連携項目マスターインポート',
                    message
                  )
                  this.dispMessageAlert(message)
                  this.saveMaster()
                })
                .catch((error) => {
                  // エラーログ登録
                  this.sendSystemLog(
                    this.LOG_LEVEL.ERROR,
                    '媒体連携項目マスターインポートエラー',
                    'マスターインポートリクエスト時に例外が発生しました。\nマスター名：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
                  )
                  if (error.error_message) {
                    this.dispErrorAlert('マスター設定のインポート時に、以下のエラーを検出しました。\n\n{0}'.format(error.error_message.join('\n')))
                  } else {
                    this.dispErrorAlert('マスター設定のインポートに失敗しました。')
                  }
                })
                .finally(() => {
                  this.importFile = null
                })
            }
            reader.readAsDataURL(this.importFile)
          } else {
              // アップロードされたファイル情報初期化
            this.importFile = null
          }
        }
      } catch (error) {
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターインポートエラー',
          'マスターインポート時に例外が発生しました。\nマスター名：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * エクスポート
     * いつでも画面上のデータをエクスポート可能
     * 1行目：共通項目名と、連携設定済みの各媒体名
     * 2行目：ログインID
     * 3行目：応募者情報項目名
     * 4行目：有効/無効設定
     * 5行目以降：項目データ
     */
    dataExport () {
      try {
        // 媒体連携項目マスターエクスポート
        this.loadingMessage = '{0}をエクスポートしています。'.format(this.currentMaster.master_name)
        // 処理中モーダルオープン
        this.isResponseWaiting = true
        this.$store.dispatch('recruitmentMedia/exportMediaCooperationItemMaster', this.currentMasterId)
          .then (() => {
            // 操作ログ登録
            this.sendSystemLog(
              this.LOG_LEVEL.OPERATION,
              '媒体連携項目マスターエクスポート',
              'マスターのエクスポートを行いました。\nマスター名：{0}'.format(this.currentMaster.master_name)
            )
          })
          .catch((error) => {
            // エラーログ登録
            this.sendSystemLog(
              this.LOG_LEVEL.ERROR,
              '媒体連携項目マスターエクスポートエラー',
              'マスターエクスポートリクエスト時に例外が発生しました。\nマスター名：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
            )
            this.dispErrorAlert('エクスポートに失敗しました。')
          })
          .finally(() => {
            // 処理中モーダルクローズ
            this.isResponseWaiting = false
          })
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターエクスポートエラー',
          'マスターエクスポート時に例外が発生しました。\nマスター名：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
        this.dispErrorAlert('エクスポートに失敗しました。')
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
      }
    },
    /**
     * インポートテンプレートダウンロード
     * 1行目：共通項目名と、連携設定済みの各媒体名
     * 2行目：ログインID
     * 3行目：応募者情報項目名 (ブランク)
     * 4行目：有効/無効設定 (デフォルトtrue)
     * 5行目以降：ブランク
     */
    templateDownload () {
      try {
        if (this.isTemplateItemSelecting === false) {
          // 編集状態遷移時初期化
          this.editInit()

          // テンプレート初期設定遷移
          this.isTemplateItemSelecting = true
          // アラートモーダル表示設定
          this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.TEMPLATE_DOWNLOAD
          this.alertData = AlertDispFunctions.createAlertData(
            'インポート用テンプレートダウンロード',
            'インポート用テンプレートをダウンロードする前に、以下の設定を行ってからダウンロードボタンをクリックしてください。\n\n・媒体連携項目マスター名の入力\n・各媒体の有効/無効設定\n・応募者情報項目選択',
            AlertDispFunctions.ALERT_DEF.ICON_TYPE.INFO,
            AlertDispFunctions.ALERT_DEF.DISP_TYPE.ALERT,
            this.$options._componentTag,
            this.ALERT_CALL_METHOD_LIST.TEMPLATE_DOWNLOAD,
            null,
            null
          )

          // アラートモーダル表示
          this.isAlertOpen = true
        } else {
          // CSVテンプレートダウンロード
          this.loadingMessage = 'CSVテンプレートをダウンロードしています。'
          // 処理中モーダルオープン
          this.isResponseWaiting = true
          this.$store.dispatch('recruitmentMedia/itemMasterCsvDownload', {currentMaster: this.currentMaster, masterName: this.masterName})
            .then (() => {
              // 操作ログ登録
              this.sendSystemLog(
                this.LOG_LEVEL.OPERATION,
                '媒体連携項目マスターテンプレートダウンロード',
                'マスターテンプレートのダウンロードを行いました。'
              )
            })
            .catch((error) => {
              // エラーログ登録
              this.sendSystemLog(
                this.LOG_LEVEL.ERROR,
                '媒体連携項目マスターテンプレートダウンロードエラー',
                'マスターテンプレートダウンロードリクエスト時に例外が発生しました。\n\n{0}'.format(error)
              )
              this.dispErrorAlert('CSVテンプレートのダウンロードに失敗しました。')
            })
            .finally(() => {
              // 処理中モーダルクローズ
              this.isResponseWaiting = false
              // 編集状態遷移時初期化
              this.editInit()
            })
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターテンプレートダウンロードエラー',
          'マスターテンプレートダウンロードリクエスト時に例外が発生しました。\n\n{0}'.format(error)
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
        this.dispErrorAlert('CSVテンプレートのダウンロードに失敗しました。')
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
      }
    },
    /**
     * 媒体連携および自動採用フロー再開
     */
    restartBatch () {
      try {
        // 確認モーダル表示設定
        this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.LINK_RESUME
        this.alertData = AlertDispFunctions.createAlertData(
          '自動連携再開',
          '媒体連携および自動採用フローを再開します。\nよろしいですか？',
          AlertDispFunctions.ALERT_DEF.ICON_TYPE.WARNING,
          AlertDispFunctions.ALERT_DEF.DISP_TYPE.CONFIRM,
          this.$options._componentTag,
          this.ALERT_CALL_METHOD_LIST.LINK_RESUME,
          null,
          null
        )
        // 確認モーダル表示
        this.isAlertOpen = true
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター自動連携再開エラー',
          '自動連携再開時に例外が発生しました。'
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
      }
    },
    /**
     * 共通項目変更
     * @param {Object} event プルダウン変更イベント
     */
    changeCommonItem (event) {
      try {
        this.currentMaster.common_item_data.header.applicant_column = Number(event.target.value)
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター共通項目変更エラー',
          '共通項目変更時に例外が発生しました。\nマスター名：{0}\n\n{1}'.format(this.currentMaster.master_name, error)
        )
      }
    },
    /**
     * 項目データ行追加
\     * @param {Number} rowNo 追加行番号 (＋ボタンを押下した次の行番号)
     */
    async addItem (rowNo) {
      try {
        this.isAddDelFlag = true
        if (this.isCreateMaster === false) {
          // 新規作成時以外は実際にレコードを追加する
          this.rowAddOrDel = true
          // 空行の追加を行う
          await this.$store.dispatch('recruitmentMedia/addItemRow', {masterId: this.currentMaster.master_id, rowNo: rowNo, isNew: false})
            .then(() => {
              // 操作ログ登録
              this.sendSystemLog(
                this.LOG_LEVEL.OPERATION,
                '媒体連携項目マスター項目データ行追加',
                '{0}の{1}行目を追加しました。'.format(this.currentMaster.master_name, rowNo)
              )
            })
            .catch((error) => {
              // エラーログ登録
              this.sendSystemLog(
                this.LOG_LEVEL.ERROR,
                '媒体連携項目マスター項目データ行追加エラー',
                '{0}の{1}行目追加時に例外が発生しました。\n\n{2}'.format(this.currentMaster.master_name, rowNo, error)
              )
              this.dispErrorAlert('行追加に失敗しました。')
            })
        } else {
          // 新規作成時はDB登録しない
          // 共通項目に行を追加
          await this.$store.dispatch('recruitmentMedia/addItemRow', {masterId: this.currentMaster.master_id, rowNo: rowNo, isNew: true})
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター項目データ行追加エラー',
          '項目データ行追加時({0}行目)に例外が発生しました。\nマスター名：{1}\n\n{2}'.format(rowNo, this.currentMaster.master_name, error)
        )
      } finally {
        this.$nextTick(() => {
          this.resizeMasterTable()
        })
        this.isAddDelFlag = false
      }
    },
    /**
     * 項目データ行削除
     * @param {Number} rowNo 削除行番号 (－ボタンを押下した行番号)
     */
    async deleteItem (rowNo) {
      try {
        this.isAddDelFlag = true
        if (this.isCreateMaster === false) {
          // 新規作成時以外はレコードを削除する
          this.rowAddOrDel = true
          // 行の削除を行う
          let delRowIdList = []
          delRowIdList.push(this.currentMaster.common_item_data.data[rowNo].id)
          this.currentMaster.media_item_data.forEach(media_data => {
            delRowIdList.push(media_data.data[rowNo].id)
          })
          await this.$store.dispatch('recruitmentMedia/delItemRow', {masterId: this.currentMaster.master_id, rowNo: rowNo, delRowIdList: delRowIdList, isNew: false})
            .then(() => {
              // 操作ログ登録
              this.sendSystemLog(
                this.LOG_LEVEL.OPERATION,
                '媒体連携項目マスター項目データ行削除',
                '{0}の{1}行目を削除しました。'.format(this.currentMaster.master_name, rowNo)
              )
            })
            .catch((error) => {
              // エラーログ登録
              this.sendSystemLog(
                this.LOG_LEVEL.ERROR,
                '媒体連携項目マスター項目データ行削除エラー',
                '{0}の{1}行目削除時に例外が発生しました。\n\n{2}'.format(this.currentMaster.master_name, rowNo, error)
              )
              this.dispErrorAlert('行の削除に失敗しました。')
            })
        } else {
          // 新規作成時はDB削除しない
          // 共通項目の行を削除
          let delRowIdList = []
          await this.$store.dispatch('recruitmentMedia/delItemRow', {masterId: this.currentMaster.master_id, rowNo: rowNo, delRowIdList: delRowIdList, isNew: true})
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスター項目データ行削除エラー',
          '項目データ行削除時({0}行目)に例外が発生しました。\nマスター名：{1}\n\n{2}'.format(rowNo, this.currentMaster.master_name, error)
        )
      } finally {
        this.$nextTick(() => {
          this.resizeMasterTable()
        })
        this.isAddDelFlag = false
      }
    },
    /**
     * メッセージアラート表示
     * @param {String} message エラーメッセージ
     */
    dispMessageAlert (message) {
      try {
        // アラートモーダル表示設定
        this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.MESSAGE_ALERT
        this.alertData = AlertDispFunctions.createAlertData(
          'メッセージ',
          message,
          AlertDispFunctions.ALERT_DEF.ICON_TYPE.INFO,
          AlertDispFunctions.ALERT_DEF.DISP_TYPE.ALERT,
          this.$options._componentTag,
          this.ALERT_CALL_METHOD_LIST.MESSAGE_ALERT,
          null,
          null
        )
        // アラートモーダル表示
        this.isAlertOpen = true
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターアラートモーダル表示エラー',
          'アラートモーダル表示時に例外が発生しました。\n表示内容：{0}\n\n{1}'.format(message, error)
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
      }
    },
    /**
     * エラーアラート表示
     * @param {String} message エラーメッセージ
     */
    dispErrorAlert (message) {
      try {
        // アラートモーダル表示設定
        this.alertDispMethod = this.ALERT_CALL_METHOD_LIST.ERROR_ALERT
        this.alertData = AlertDispFunctions.createAlertData(
          'エラーが発生しました',
          message,
          AlertDispFunctions.ALERT_DEF.ICON_TYPE.DANGER,
          AlertDispFunctions.ALERT_DEF.DISP_TYPE.ALERT,
          this.$options._componentTag,
          this.ALERT_CALL_METHOD_LIST.ERROR_ALERT,
          null,
          null
        )
        // アラートモーダル表示
        this.isAlertOpen = true

      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターアラートモーダル表示エラー',
          'アラートモーダル表示時に例外が発生しました。\n表示内容：{0}\n\n{1}'.format(message, error)
        )
        // 確認モーダルクローズ
        this.isAlertOpen = false
      }
    },
    /**
     * アラートモーダルでのボタン押下結果
     * @param {Object} resultData ボタン押下結果
     */
    async alertResult (resultData) {
      try {
        // アラートモーダルクローズ
        this.isAlertOpen = false
        if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.KEY_FUNCTION] === this.$options._componentTag) {
          if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.KEY_WORD] === this.alertDispMethod) {
            // 呼び出したメソッドごとの処理
            if (this.alertDispMethod === this.ALERT_CALL_METHOD_LIST.LINK_RESUME) {
              // 自動連携再開
              if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE] === AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES) {
                // 媒体連携と自動採用フローを再開
                this.linkResume()
              }
            } else if (this.alertDispMethod === this.ALERT_CALL_METHOD_LIST.LINK_SUSPEND) {
              // 自動連携一時停止
              if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE] === AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES) {
                // 媒体連携および自動採用フロー一時停止API
                this.loadingMessage = '媒体連携および自動採用フローを停止しています。'
                this.isResponseWaiting = true
                await this.$store.dispatch('background/batchTaskSuspend', this.$options._componentTag)
                  .catch((error) => {
                    // エラーログ登録
                    this.sendSystemLog(
                      this.LOG_LEVEL.ERROR,
                      '媒体連携項目マスター自動連携一時停止エラー',
                      '自動連携一時停止リクエスト時に例外が発生しました。\n処理メソッド：{0}\n\n{1}'
                        .format(this.alertDispMethod, error)
                    )
                    this.dispErrorAlert('媒体連携および自動採用フローの停止に失敗しました。')
                  })
                  .finally(() => {
                    // 後続処理
                    this.linkSuspendSubsequent(resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE])
                    // 処理中モーダルクローズ
                    this.isResponseWaiting = false
                  })
              } else {
                // 後続処理
                this.linkSuspendSubsequent(resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE])
              }
            } else if (this.alertDispMethod === this.ALERT_CALL_METHOD_LIST.DELETE_MASTER) {
              // 媒体連携項目マスター削除
              if (resultData[AlertDispFunctions.ALERT_DEF.RESULT_TEMPLATE_KEY_NAME.VALUE] === AlertDispFunctions.ALERT_DEF.RESULT_TYPE.YES) {
                // 連携処理を一時停止する
                this.linkSuspend(this.ALERT_CALL_METHOD_LIST.DELETE_MASTER)
              }
            }
          }
        }
      } catch (error) {
        // エラーログ登録
        this.sendSystemLog(
          this.LOG_LEVEL.ERROR,
          '媒体連携項目マスターアラートモーダル結果エラー',
          'アラートモーダル結果処理時に例外が発生しました。\n処理メソッド：{0}\n処理結果：{1}\n\n{2}'
            .format(this.alertDispMethod, resultData, error)
        )
        // 処理中モーダルクローズ
        this.isResponseWaiting = false
        // 一時停止要求メソッド名初期化
        this.suspendMethodName = ''
      } finally {
        // アラートモーダルクローズ
        this.isAlertOpen = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// モーダル幅
@mixin modal-width {
  width: calc(100vw - 120px);
}
.master-setting-modal {
  @include modal-width;
  // height: calc(100vh - 125px) !important;
  // 上部操作パネル
  .master-setting-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 16px;
    // マスター名選択
    .master-select-area {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: auto;
      // マスター名表示
      .name-disp {
        position: relative;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        // マスター選択プルダウン
        .setting-select {
          width: 200px;
          height: 30px;
        }
        // マスター名変更ボタン
        .name-edit {
          width: 100px;
        }
        // 有効フラグ
        .toggle-apply-flag {
          width: 100px;
        }
        // マスター名入力ボックス
        .setting-name {
          padding-right: 48px;
          width: 200px;
          height: 30px;
        }
        // マスター名保存ボタン
        .name-save {
          width: 100px;
        }
        // マスター名変更キャンセルボタン
        .name-edit-cancel {
          width: 100px;
        }
        .master-order {
          width: 50px;
        }
      }
    }
    // 操作ボタン類
    .operation-button-area {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: auto;
      // 媒体項目マスター設定名編集
      .master-edit-area {
        padding-left: 32px;
        position: relative;
        width: auto;
        // 有効切替ボタン
        .toggle-apply {
          width: 100px;
        }
        // マスター編集ボタン
        .master-edit {
          width: 100px;
        }
        // マスター新規作成ボタン
        .master-create {
          width: 100px;
        }
        // マスター削除ボタン
        .master-delete {
          width: 100px;
        }
        // マスター編集キャンセルボタン
        .master-edit-cancel {
          width: 100px;
        }
      }
      // インポート/エクスポート
      .data-in-out-area {
        position: relative;
        padding-left: 32px;
        max-width: auto;
        // インポートボタン
        .data-import {
          width: 100px;
        }
        // エクスポートボタン
        .data-export {
          width: 100px;
        }
        // テンプレートボタン
        .template-download {
          width: 100px;
        }
      }
      // 自動連携再開
      .resume-area {
        width: auto;
        position: relative;
        padding-left: 32px;
        // 自動連携再開ボタン
        .resume-alignment {
          width: 100px;
        }
      }
      // 一時停止中メッセージ
      .suspend-message {
        color: red;
        padding-left: 12px;
        font-size: 10px;
        // font-size: smaller;
      }
      // 閉じるボタン
      .master-setting-close-area {
        width: auto;
        position: relative;
        padding-left: 50px;
        // 閉じるボタン
        .master-setting-close {
          width: 100px;
        }
      }
    }
  }
  // 共通項目データ設定
  .master-setting-body {
    @include modal-width;
    height: calc(100vh - 180px);
    display: flex;
    // 奇数行背景色
    @mixin row-color-odd {
      background-color:#ffeeee;
    }
    // 偶数行背景色
    @mixin row-color-even {
      background-color:#fcfcfc;
    }
    // セル枠
    @mixin cell-border {
      border: solid #000 1px;
    }

    // マスター設定項目
    .master-setting-items {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
    }
    .master-setting-item {
      margin: 10px 16px;
    }
    fieldset:disabled .master-setting-item {
      opacity: 0.5;
    }

    // 項目設定テーブル
    .item-table {
      display: table;
      table-layout: fixed;
      border-collapse: separate;
    }
    // 背景色
    .item-table tr td {
      @include cell-border;
    }
    // No.ヘッダーセルサイズ
    @mixin th-no {
      min-width: 50px;
      max-width: 50px;
      height: 30px;
    }
    // 追加/削除ヘッダーセルサイズ
    @mixin th-add {
      min-width: 100px;
      max-width: 100px;
      height: 30px;
    }
    // 項目データヘッダーセルサイズ
    @mixin th-item {
      min-width: 250px;
      max-width: 250px;
      height: 30px;
    }
    // データセルサイズ
    .item-table td {
      width: 250px;
      height: 30px;
    }
    // 1行目固定 (縦スクロール対策)
    .item-table thead th {
      @include row-color-odd;
      @include cell-border;
      text-align: center;
      background-clip: padding-box;
      position: -webkit-sticky;
      position: sticky;
      top: 0;
      z-index: 3;
    }
    // No.列ヘッダー固定 (横スクロール対策)
    .th-header-no {
      @include cell-border;
      @include th-no;
      text-align: center;
      position: -webkit-sticky;
      position: sticky;
      left: 0px;
      z-index: 3 !important;
    }
    // 追加/削除列ヘッダー固定 (横スクロール対策)
    .th-header-add {
      @include cell-border;
      @include th-add;
      text-align: center;
      position: -webkit-sticky;
      position: sticky;
      left: 50px;
      z-index: 3 !important;
    }
    // 共通項目列ヘッダー固定 (横スクロール対策)
    .th-header-common {
      @include cell-border;
      @include th-item;
      text-align: center;
      position: -webkit-sticky;
      position: sticky;
      z-index: 3 !important;
    }
    // 共通項目列ヘッダー固定 (横スクロール対策)
    @mixin th-header-stiky {
      @include cell-border;
      @include th-item;
      position: -webkit-sticky;
      position: sticky;
    }
    // 未編集時共通項目列ヘッダー固定 (横スクロール対策)
    .th-header-1 {
      @include th-header-stiky;
      left: 50px !important;
      z-index: 2 !important;
    }
    // 編集時共通項目列ヘッダー固定 (横スクロール対策)
    .th-header-2 {
      @include th-header-stiky;
      left: 150px !important;
      z-index: 2 !important;
    }
    // 未編集時媒体項目列ヘッダー固定 (横スクロール対策)
    .th-header-3 {
      @include th-header-stiky;
      left: 50px !important;
      z-index: 1 !important;
    }
    // 編集時媒体項目列ヘッダー固定 (横スクロール対策)
    .th-header-4 {
      @include th-header-stiky;
      left: 150px !important;
      z-index: 1 !important;
    }
    // 追加/削除列ボディー固定 (横スクロール対策)
    .td-item-no {
      @include cell-border;
      @include th-no;
      text-align: center;
      position: -webkit-sticky;
      position: sticky;
      left: 0px;
      z-index: 2;
    }
    // 追加/削除列ボディー固定 (横スクロール対策)
    .td-item-add {
      @include cell-border;
      @include th-add;
      text-align: center;
      position: -webkit-sticky;
      position: sticky;
      left: 50px;
      z-index: 2;
    }
    // 共通項目列ボディー固定 (横スクロール対策)
    .td-item-common {
      @include cell-border;
      @include th-item;
      text-align: center;
      position: -webkit-sticky;
      position: sticky;
      z-index: 1;
    }
    // 共通項目列ボディー固定 (横スクロール対策)
    @mixin td-item-stiky {
      @include cell-border;
      @include th-item;
      position: -webkit-sticky;
      position: sticky;
    }
    // 未編集時共通項目列ボディー固定 (横スクロール対策)
    .td-item-1 {
      @include td-item-stiky;
      left: 50px !important;
      z-index: 1 !important;
    }
    // 編集時共通項目列ボディー固定 (横スクロール対策)
    .td-item-2 {
      @include td-item-stiky;
      left: 150px !important;
      z-index: 1 !important;
    }
    // 未編集時共通項目列ボディー固定 (横スクロール対策)
    .td-item-3 {
      @include td-item-stiky;
      left: 50px !important;
      z-index: 0 !important;
    }
    // 編集時共通項目列ボディー固定 (横スクロール対策)
    .td-item-4 {
      @include td-item-stiky;
      left: 150px !important;
      z-index: 0 !important;
    }
    // 奇数データ行
    .data-row-odd {
      @include row-color-odd;
      z-index: 0;
    }
    // 偶数データ行
    .data-row-even {
      @include row-color-even;
      z-index: 0;
    }
    .common-name {
      padding-top: 4px;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: smaller;
      font-weight: 600;
    }
    .media-name {
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: smaller;
      font-weight: 600;
    }
    .media-account-th {
      display: inline-block;
      // text-align: center;
      // justify-content: flex-start;
      align-items: left;
    }
    .media-name-cell {
      display: flex;
    }
    // 共通項目選択プルダウン
    .common-item-select {
      width: 90%;
      margin: 4px;
      font-size: smaller;
      /deep/ .vs__selected-options {
        font-size: smaller;
      }
    }
    // 項目データ増減ボタン
    .fluctuation-button {
      width: 20px;
      height: 27px;
      background: inherit;
      border: none;
      top: 0;
      bottom: 0;
      right: 0;
    }
    // 媒体項目選択プルダウン
    .media-item-select {
      width: 180px;
      height: 25px;
      margin: 4px;
    }
    // 各データ入力ボックス
    .table-cell {
      width: 100%;
      background: inherit;
      border: none;
      border-radius: 0;
    }
  }
  .table-footer {
    width: calc(100% - 20px);
    margin: 0 auto;
    height: 50px;
    display: flex;
    align-items: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/hrm-frontend/views/atomic/screen-components/applicant-components/atomic-templates/media-cooperation/MediaCooperationItemMasterSetting.vue